import * as React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { type ZoneRow } from './allureTable.ts';
import { versIntervalsIcu } from './intervalsIcu.ts';

type Props = {
  /** Notation deja ajustee (ajustement des recuperations applique). */
  notation: string;
  vma: number;
  table: ZoneRow[];
  /** Modele de seance par defaut applique (nom et notations ajustees de ses parties). */
  nomModele?: string;
  echauffement?: string;
  retourCalme?: string;
};

function minutes(valeur: string): number {
  const n = parseInt(valeur, 10);
  return isNaN(n) || n < 0 ? 0 : Math.min(n, 120);
}

/**
 * Apercu de la seance au format texte intervals.icu (a coller dans
 * l'editeur de seance d'intervals.icu, en attendant l'envoi direct).
 */
export default function ApercuIntervalsIcu({ notation, vma, table, nomModele, echauffement, retourCalme }: Props) {
  const avecModele = !!(echauffement || retourCalme);
  // Sans modele de seance : echauffement / retour au calme en minutes.
  const [echauffementMin, setEchauffementMin] = React.useState('0');
  const [retourCalmeMin, setRetourCalmeMin] = React.useState('0');
  const [copie, setCopie] = React.useState(false);

  const resultat = React.useMemo(
    () =>
      versIntervalsIcu(notation, vma, table, {
        echauffementMin: minutes(echauffementMin),
        retourCalmeMin: minutes(retourCalmeMin),
        echauffement,
        retourCalme,
      }),
    [notation, vma, table, echauffementMin, retourCalmeMin, echauffement, retourCalme],
  );

  const copier = () => {
    if (!resultat.texte) return;
    navigator.clipboard
      .writeText(resultat.texte)
      .then(() => {
        setCopie(true);
        setTimeout(() => setCopie(false), 2000);
      })
      .catch(() => setCopie(false));
  };

  return (
    <Accordion variant="outlined" sx={{ mt: 3 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: 600 }}>Format intervals.icu</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2, alignSelf: 'center' }}>
          séance structurée pour la montre
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          {avecModele ? (
            <Typography variant="body2" color="text.secondary">
              Échauffement et récupération de ton modèle « {nomModele} »
            </Typography>
          ) : (
            <>
              <TextField
                label="Échauffement (min)"
                type="number"
                size="small"
                value={echauffementMin}
                onChange={(e) => setEchauffementMin(e.target.value)}
                inputProps={{ min: 0, max: 120 }}
                sx={{ width: 170 }}
              />
              <TextField
                label="Retour au calme (min)"
                type="number"
                size="small"
                value={retourCalmeMin}
                onChange={(e) => setRetourCalmeMin(e.target.value)}
                inputProps={{ min: 0, max: 120 }}
                sx={{ width: 180 }}
              />
            </>
          )}
          <Button startIcon={<ContentCopyIcon />} onClick={copier} disabled={!resultat.texte}>
            {copie ? 'Copié !' : 'Copier'}
          </Button>
        </Box>
        {resultat.erreur ? (
          <Alert severity="error">{resultat.erreur}</Alert>
        ) : (
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              bgcolor: 'action.hover',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: 14,
              whiteSpace: 'pre-wrap',
            }}
          >
            {resultat.texte}
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
          Allures cibles calculées avec ta VMA ({vma} km/h) et tes paramètres d'allures, en plage allant de l'allure indiquée à l'allure immédiatement plus lente (ex. @5K entre l'allure 5K et l'allure 10K) ; les récupérations sont seulement plus lentes que l'allure de ta zone Récupération.
          Les répétitions imbriquées sont mises à plat (intervals.icu ne les gère pas).
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}
